'use client';

import { useEffect, useState, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../../assets/logo.png';
import { FiBell, FiLogOut, FiX } from 'react-icons/fi';
import { MdOutlineFaceUnlock } from 'react-icons/md';
import { FaCaretDown } from 'react-icons/fa';
import MenuToggleIcon from './MenuToggleIcon';
import {
  clearLeads,
  setLeadsPage,
} from '../../../redux/features/adminLeadSlice';
import { setPage as setApplicationLogPage } from '../../../redux/features/applicationLogSlice';
import { useCountries } from '../../client/dashboard/staticData';
import { useSelector, useDispatch } from 'react-redux';
import { setGlobalCountries } from '../../../redux/features/countriesSlice';
import socketService from '../../../config/socketService';
import apiFetch from '../../../utlis/apiFetch';
import {
  addNotification,
  clearNotifications,
  setNotifications,
  updateNotification,
} from '../../../redux/features/notificationSlice';
import { logout } from '../../../redux/features/logoutSlice';

const navItems = [
  { name: 'Dashboard', path: '/admin', end: true },
  { name: 'Leads', path: '/admin/leads' },
  // { name: "Finances", path: "/admin/finances" },
  { name: 'Client History', path: '/admin/client-history' },
  { name: 'Application Log', path: '/admin/application-log' },
  { name: 'Employees', path: '/admin/employees' },
  { name: 'Documents', path: '/admin/documents' },
  { name: 'Marketing', path: '/admin/marketing' },
];

export default function Navbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const countryDropdownRef = useRef(null);
  const countries = useCountries();
  const [selectedCountries, setSelectedCountries] = useState([]);
  const globalCountries = useSelector(
    (state) => state.globalCountries.globalCountries,
  );
  const hideDropdown =
    location.pathname.includes('employees') ||
    location.pathname.includes('documents');

  const { notifications, isLoaded } = useSelector(
    (state) => state.notifications,
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const notificationRef = useRef(null);

  useEffect(() => {
    console.log(globalCountries);
    setSelectedCountries(globalCountries);
  }, [globalCountries]);

  useEffect(() => {
    if (!isOpen) return;

    const handleCountryOutsideClick = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleCountryOutsideClick);
    document.addEventListener('touchstart', handleCountryOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleCountryOutsideClick);
      document.removeEventListener('touchstart', handleCountryOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isLoaded) {
      fetchNotifications();
    }
    setupSocketConnection();
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLoaded]);

  useEffect(() => {
    if (!isNotificationOpen) return;

    const handleNotificationOutsideClick = (event) => {
      const clickedNotificationTrigger = event.target.closest(
        '[data-notification-trigger="true"]',
      );
      if (clickedNotificationTrigger) return;
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
        document.body.style.overflow = '';
      }
    };

    document.addEventListener('mousedown', handleNotificationOutsideClick);
    document.addEventListener('touchstart', handleNotificationOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleNotificationOutsideClick);
      document.removeEventListener(
        'touchstart',
        handleNotificationOutsideClick,
      );
    };
  }, [isNotificationOpen]);

  useEffect(() => {
    if (notifications) {
      const unreadNotifications = notifications.filter((n) => !n.isRead);
      setUnreadCount(unreadNotifications.length);
    }
  }, [notifications]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/notifications`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.ok) {
        const data = response._data ?? (await response.json());
        dispatch(setNotifications(data.body));
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const setupSocketConnection = () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return;
      const user = JSON.parse(userData);
      socketService.connect(user._id);
      socketService.setupNotificationListener(user._id, (newNotification) => {
        console.log('+++++++++++', newNotification);
        dispatch(addNotification(newNotification.notification));
      });
    } catch (error) {
      console.error('Error setting up socket connection:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/notifications/readNotification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ notificationId }),
        },
      );
      if (response.ok) {
        dispatch(updateNotification({ id: notificationId }));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) markAsRead(notification._id);
    if (notification.link) {
      navigate(notification.link);
      setIsNotificationOpen(false);
    }
  };

  const toggleNotifications = () => {
    setIsNotificationOpen((prev) => !prev);
    if (!isNotificationOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  const handleCheckboxChange = (countryName) => {
    setSelectedCountries((prevSelected) => {
      const updatedSelection = prevSelected.includes(countryName)
        ? prevSelected.filter((name) => name !== countryName)
        : [...prevSelected, countryName];
      dispatch(setGlobalCountries(updatedSelection));
      setIsOpen(false);
      return updatedSelection;
    });
  };

  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        navigate('/');
        return;
      }
      const parsedUser = JSON.parse(userData);
      // Allow both admin and sub-admin roles to access admin area
      if (!(parsedUser?.role === 'admin' || parsedUser?.role === 'sub-admin')) {
        // eslint-disable-next-line no-console
        console.warn('Navbar: redirecting because user role is not admin/sub-admin', parsedUser?.role)
        navigate('/');
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    dispatch(logout());
    dispatch(clearLeads());
    dispatch(clearNotifications());
    socketService.disconnect();
    navigate('/');
  };

  const handleNavClick = (item) => {
    if (item.path.includes('/leads')) {
      dispatch(setLeadsPage(1));
    }
    if (item.path.includes('/application-log')) {
      dispatch(setApplicationLogPage(1));
    }
  };

  const getNavTarget = (item) => {
    if (item.path.includes('/application-log')) {
      return { pathname: item.path, search: '?page=1' };
    }
    return { pathname: item.path, search: '' };
  };

  const handleLogoClick = () => navigate('/admin');

  return (
    <header className="bg-[#005787] text-white px-4 xl:px-6 py-4 lg:py-6 relative">
      {/* ── Main row ── */}
      <div className="flex items-center justify-between gap-2">
        {/* Logo — fixed width so it never squeezes the nav */}
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={handleLogoClick}
            className="cursor-pointer"
            aria-label="Go to dashboard"
          >
            <img
              src={logo || '/placeholder.svg'}
              loading="lazy"
              alt="World Class Logo"
              width={110}
              height={40}
              className="xl:w-[120px]"
            />
          </button>
        </div>

        {/* Desktop Navigation — grows to fill available space, wraps if needed */}
        <nav className="hidden lg:flex flex-1 min-w-0 justify-center">
          <ul className="flex flex-wrap justify-center items-center gap-x-2 xl:gap-x-4 gap-y-1">
            {navItems.map((item) => (
              <li key={item.name} className="flex-shrink-0">
                <NavLink
                  to={getNavTarget(item)}
                  end={item.end}
                  onClick={() => handleNavClick(item)}
                  className={({ isActive }) =>
                    `cursor-pointer transition-colors duration-200 relative pb-1 text-[13px] xl:text-sm whitespace-nowrap ${
                      isActive
                        ? 'text-white after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-[40px] after:h-[3px] after:bg-[#87CA60] after:rounded-sm'
                        : ''
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Desktop Right Section — fixed width, never grows */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink-0">
          {/* Country Dropdown */}
          <div
            ref={countryDropdownRef}
            className="relative text-black flex-shrink-0"
          >
            <AnimatePresence initial={false}>
              {!hideDropdown && (
                <motion.div
                  key="country-trigger"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.2 }}
                >
                  <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-1.5 rounded-full px-2 py-1.5 text-xs xl:text-sm border border-[#87CA60]/60 bg-white drop-shadow-md"
                    style={{ minWidth: '90px', maxWidth: '160px' }}
                  >
                    {selectedCountries.length > 0 ? (
                      <div className="flex items-center gap-0.5 overflow-hidden max-w-[90px]">
                        {selectedCountries.length <= 8 &&
                          selectedCountries.map((name) => {
                            const country = countries.find(
                              (c) => c.name === name,
                            );
                            if (!country) return null;
                            return (
                              <img
                                key={country.name}
                                src={country.flag || '/placeholder.svg'}
                                alt={country.name}
                                className="w-5 h-5 rounded-full object-cover border border-slate-300 flex-shrink-0"
                              />
                            );
                          })}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-xs whitespace-nowrap">
                        Select country
                      </span>
                    )}
                    <svg
                      className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        key="country-menu"
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 4, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-0 top-full mt-1 w-[200px] bg-white border rounded-lg shadow-lg py-1 z-10"
                      >
                        {countries.map((country) => (
                          <label
                            key={country.name}
                            className="w-full flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 space-x-2"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCountries.includes(country.name)}
                              onChange={() =>
                                handleCheckboxChange(country.name)
                              }
                              className="cursor-pointer"
                            />
                            <img
                              src={country.flag || '/placeholder.svg'}
                              alt={country.name}
                              loading="lazy"
                              className="w-5 h-5 rounded-full object-cover"
                            />
                            <span>{country.name}</span>
                          </label>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Notification Bell */}
          <div
            className="relative flex-shrink-0"
            data-notification-trigger="true"
          >
            <FiBell
              className="cursor-pointer text-[#87CA60] h-5 w-5 xl:h-6 xl:w-6"
              strokeWidth={3}
              onClick={toggleNotifications}
            />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>

          {/* User Menu */}
          <div className="relative group text-slate-800 flex-shrink-0">
            <div className="flex items-center gap-1.5 bg-[#87CA60] px-2 py-1.5 rounded-full cursor-pointer">
                <MdOutlineFaceUnlock className="h-5 w-5 bg-white text-slate-700 rounded-full flex-shrink-0" />
                <span className="font-medium text-xs xl:text-sm max-w-[72px] truncate">Hello Admin</span>
                <FaCaretDown className="text-white w-3.5 h-3.5 flex-shrink-0" />
            </div>

            {/* Dropdown */}
            <div className="absolute z-50 right-0 w-48 bg-transparent pt-2">
              <div className="bg-[#87CA60] shadow-lg rounded-lg hidden group-hover:block">
                <ul className="py-2">
                  <li
                    onClick={() => navigate('/admin/settings')}
                    className="px-4 py-2 hover:bg-gray-100/10 cursor-pointer text-sm"
                  >
                    Settings
                  </li>
                  <li
                    onClick={handleLogout}
                    className="px-4 py-2 hover:bg-gray-100/10 cursor-pointer text-sm"
                  >
                    Logout
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex-shrink-0">
          <MenuToggleIcon
            isOpen={isMobileMenuOpen}
            setIsOpen={setIsMobileMenuOpen}
          />
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden"
          >
            <div className="mt-4 pb-4">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="flex items-center space-x-2 mb-4"
              />
              <nav>
                <ul className="space-y-2">
                  {navItems.map((item, index) => (
                    <motion.li
                      key={item.name}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <NavLink
                        to={getNavTarget(item)}
                        end={item.end}
                        onClick={() => {
                          handleNavClick(item);
                          setIsMobileMenuOpen(false);
                        }}
                        className={({ isActive }) =>
                          `block hover:bg-white/10 px-2 py-1 rounded cursor-pointer relative ${
                            isActive
                              ? 'bg-white/10 text-white font-medium after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:bg-white'
                              : ''
                          }`
                        }
                      >
                        {item.name}
                      </NavLink>
                    </motion.li>
                  ))}
                  <motion.li
                    key="Settings"
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 6 * 0.1 }}
                  >
                    <NavLink
                      to="/admin/settings"
                      end={'/admin/settings' === '/admin'}
                      className={({ isActive }) =>
                        `block hover:bg-white/10 px-2 py-1 rounded cursor-pointer relative ${
                          isActive
                            ? 'bg-white/10 text-white font-medium after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:bg-white'
                            : ''
                        }`
                      }
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Settings
                    </NavLink>
                  </motion.li>
                </ul>
              </nav>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center justify-between mt-4 pt-4 border-t border-white/20"
              >
                {/* Mobile Notification Icon */}
                <div className="relative" data-notification-trigger="true">
                  <FiBell
                    className="cursor-pointer text-[#87CA60] h-6 w-6"
                    strokeWidth={3}
                    onClick={toggleNotifications}
                  />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span>Hello {'Admin'}</span>
                  <MdOutlineFaceUnlock className="h-6 w-6 bg-white text-slate-700 rounded-full" />
                </div>
                <button
                  onClick={handleLogout}
                  className="bg-white text-red-500 flex flex-row items-center gap-3 p-2 rounded-lg"
                >
                  <FiLogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Notification Panel ── */}
      <AnimatePresence>
        {isNotificationOpen && (
          <motion.div
            ref={notificationRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-[80px] right-4 md:right-6 lg:absolute lg:top-full lg:right-0 w-[90%] md:w-96 bg-[#FAF6F1] border border-gray-200 rounded-lg shadow-lg z-[1000] max-h-[80vh] flex flex-col"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          >
            <div className="p-4 border-b border-gray-200 bg-[#005787] text-white flex items-center justify-between rounded-t-lg md:rounded-t-none">
              <h3 className="text-lg font-medium">Notifications</h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setIsNotificationOpen(false)}
                  className="p-1 rounded-full hover:bg-[#87CA60] transition-colors"
                  aria-label="Close notifications"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications && notifications.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !notification.isRead ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start">
                        {!notification.isRead && (
                          <span className="h-2 w-2 mt-1.5 rounded-full bg-blue-500 mr-2 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm text-gray-800">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(notification.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  No notifications
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
